"use client";

import { JSX } from "react";
import { useCallDuration } from "./hooks/useCallDuration";
import { useAppSelector } from "@client/store/store.hooks";
import { useTranslations } from "next-intl";

type CallDurationBadgeProps = {
  variant?: "toolbar";
};

export const CallDurationBadge = ({ variant }: CallDurationBadgeProps): JSX.Element | null => {
  const connectionDetails = useAppSelector((state) => state.connection.connectionDetails);
  const { formattedDuration, isConnected } = useCallDuration(connectionDetails?.callStartedAt);
  const t = useTranslations();

  if (!isConnected) return null;

  if (variant === "toolbar") {
    return (
      <div
        className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-1.5 text-white shadow-sm"
        role="status"
        aria-live="polite"
      >
        <div className="relative shrink-0">
          <div className="absolute inset-0 animate-ping rounded-full bg-green-500 opacity-40" />
          <div className="relative h-2 w-2 rounded-full bg-green-500" />
        </div>
        <span className="hidden text-[11px] text-slate-300 sm:inline">
          {t("meeting.liveMeeting", { defaultValue: "Live Meeting" })}
        </span>
        <span className="font-mono text-xs font-semibold tabular-nums tracking-wide sm:text-sm">
          {formattedDuration}
        </span>
      </div>
    );
  }

  return (
    <div
      className="
      flex items-center gap-3
      px-4 py-2.5
      rounded-xl
      bg-slate-900/80
      backdrop-blur-md
      border border-slate-700
      shadow-xl
      text-white
    "
    >
      <div className="relative">
        <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-50" />
        <div className="relative h-3 w-3 rounded-full bg-green-500" />
      </div>

      <div className="flex flex-col">
        <span className="text-[13px] text-slate-300">{t("meeting.liveMeeting", { defaultValue: "Live Meeting" })}</span>

        <span className="font-mono text-sm font-bold tracking-wider tabular-nums">{formattedDuration}</span>
      </div>
    </div>
  );
};

type BreakoutToolbarTimersProps = {
  timerRemainingSec: number | null | undefined;
  closeCountdownSec: number | null | undefined;
  onReturnNow?: () => void;
};

export const BreakoutToolbarTimers = ({
  timerRemainingSec,
  closeCountdownSec,
  onReturnNow
}: BreakoutToolbarTimersProps): JSX.Element | null => {
  const t = useTranslations("breakoutRooms");

  if (closeCountdownSec != null) {
    return (
      <div
        className="flex max-w-[11rem] flex-col rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-amber-950"
        role="status"
        aria-live="polite"
      >
        <span className="text-[11px] font-medium leading-tight">
          {t("closeCountdown", { seconds: closeCountdownSec })}
        </span>
        {onReturnNow ? (
          <button
            type="button"
            className="mt-0.5 cursor-pointer text-left text-[11px] font-semibold underline disabled:cursor-not-allowed"
            onClick={onReturnNow}
          >
            {t("returnNow")}
          </button>
        ) : null}
      </div>
    );
  }

  if (timerRemainingSec == null) return null;

  const minutes = Math.floor(timerRemainingSec / 60);
  const seconds = String(timerRemainingSec % 60).padStart(2, "0");

  return (
    <div
      className="flex items-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50 px-2.5 py-1.5 text-sky-950"
      role="status"
      aria-live="polite"
    >
      <span className="text-[11px] font-medium leading-tight sm:hidden">
        {minutes}:{seconds}
      </span>
      <span className="hidden text-[11px] font-medium leading-tight sm:inline">
        {t("timerRemaining", { minutes, seconds })}
      </span>
    </div>
  );
};
